import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQSection from "@/components/FAQSection";
import { CITY_FAQS } from "@/data/faqs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { MapPin, TrendingUp, Users, BadgeCheck, Sparkles } from "lucide-react";

// City → landmark, state, hero image, and popular localities used for SEO copy
const CITY_META = {
  bengaluru: { state: "Karnataka", landmark: "Silicon Valley of India", hero: "photo-1596176530529-78163a4f7af2", localities: ["Indiranagar", "Whitefield", "Koramangala", "HSR Layout", "Marathahalli", "Electronic City", "JP Nagar", "BTM Layout"] },
  mumbai:    { state: "Maharashtra", landmark: "Gateway of India", hero: "photo-1570168007204-dfb528c6958f", localities: ["Andheri West", "Powai", "Bandra", "Thane", "Malad", "Goregaon", "Chembur", "Navi Mumbai"] },
  delhi:     { state: "Delhi NCR", landmark: "India Gate", hero: "photo-1587474260584-136574528ed5", localities: ["South Delhi", "Dwarka", "Rohini", "Saket", "Vasant Kunj", "Karol Bagh", "Lajpat Nagar", "Greater Kailash"] },
  pune:      { state: "Maharashtra", landmark: "Cultural capital of Maharashtra", hero: "photo-1705955463252-e3f670e4041b", localities: ["Kothrud", "Hinjewadi", "Wakad", "Baner", "Kharadi", "Viman Nagar", "Aundh", "Hadapsar"] },
  hyderabad: { state: "Telangana", landmark: "Charminar & HITEC City", hero: "photo-1741545979534-02f59c742730", localities: ["Gachibowli", "Madhapur", "Kondapur", "Hitech City", "Kukatpally", "Jubilee Hills", "Banjara Hills", "Miyapur"] },
  chennai:   { state: "Tamil Nadu", landmark: "Marina Beach & Kapaleeshwarar Temple", hero: "photo-1582510003544-4d00b7f74220", localities: ["Anna Nagar", "T. Nagar", "Adyar", "Velachery", "OMR", "Porur", "Nungambakkam", "Perungudi"] },
  gurgaon:   { state: "Haryana", landmark: "Cyber City", hero: "photo-1697135376181-f6b7aac6caf6", localities: ["DLF Phase 1-5", "Sohna Road", "Golf Course Road", "Sector 56", "Sector 82", "MG Road", "Udyog Vihar", "Cyber City"] },
  jodhpur:   { state: "Rajasthan", landmark: "Mehrangarh Fort & Blue City", hero: "photo-1669548329838-45008f9a45fc", localities: ["Ratanada", "Sardarpura", "Chopasni Housing Board", "Paota", "Shastri Nagar", "Basni", "Air Force Area", "New Power House Road"] },
  jaipur:    { state: "Rajasthan", landmark: "Hawa Mahal & Amer Fort", hero: "photo-1599661046289-e31897846e41", localities: ["Malviya Nagar", "Mansarovar", "Vaishali Nagar", "C-Scheme", "Raja Park", "Jagatpura", "Tonk Road", "Sirsi Road"] },
  udaipur:   { state: "Rajasthan", landmark: "Lake Palace & Lake Pichola", hero: "photo-1695956353120-54ce5e91632b", localities: ["Fatehpura", "Hiran Magri", "Bhuwana", "Sector 4", "Sector 11", "Rani Road", "Sisarma", "Bedla"] },
  kolkata:   { state: "West Bengal", landmark: "Victoria Memorial & Howrah Bridge", hero: "photo-1558431382-27e303142255", localities: ["Salt Lake", "New Town", "Ballygunge", "Alipore", "Behala", "Jadavpur", "Park Street", "Rajarhat"] },
  ahmedabad: { state: "Gujarat", landmark: "Sabarmati Riverfront", hero: "photo-1687840430404-b82859282de4", localities: ["Bopal", "SG Highway", "Prahlad Nagar", "Satellite", "Bodakdev", "Naranpura", "Vastrapur", "Chandkheda"] },
  goa:       { state: "Goa", landmark: "Beach paradise", hero: "photo-1512343879784-a960bf40e7f2", localities: ["Panaji", "Margao", "Vasco", "Calangute", "Anjuna", "Baga", "Candolim", "Porvorim"] },
  agra:      { state: "Uttar Pradesh", landmark: "Taj Mahal", hero: "photo-1548013146-72479768bada", localities: ["Dayalbagh", "Kamla Nagar", "Sikandra", "Tajganj", "Sadar Bazar", "Shahganj", "Khandari", "Balkeshwar"] },
  varanasi:  { state: "Uttar Pradesh", landmark: "Ganga Ghats & Kashi Vishwanath", hero: "photo-1706186839147-0d708602587b", localities: ["Sigra", "Sunderpur", "Lanka", "Bhelupur", "Cantt", "Mahmoorganj", "Nadesar", "Assi"] },
};

export default function CityLandingPage() {
  const { city } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const key = (city || "").toLowerCase();
  const meta = CITY_META[key];
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/properties", { params: { city: key, listing_type: "rent", limit: 60 } })
      .then(({ data }) => {
        setItems(data.slice(0, 12));
        setCount(data.length);
      })
      .finally(() => setLoading(false));
  }, [key]);

  if (!meta) {
    return (
      <div className="max-w-4xl mx-auto p-16 text-center">
        <p className="font-display text-3xl text-[#7A1E2A] mb-3">We're not in {city} yet</p>
        <p className="text-gray-600 mb-6">Browse all cities where To-Let Rentals is active.</p>
        <Link to="/properties" className="tlr-gold-btn rounded-full px-6 py-3 inline-block">Browse all</Link>
      </div>
    );
  }

  const cityCap = key.charAt(0).toUpperCase() + key.slice(1);
  const title = `Homes for Rent in ${cityCap} — from Verified Owners`;
  const description = `Find ${cityCap} flats and houses for rent directly from owners. ${count > 0 ? `${count} live listings` : "Fresh listings"} across ${meta.localities.slice(0, 3).join(", ")} and more. Zero brokerage. Free forever.`;
  const heroImg = `https://images.unsplash.com/${meta.hero}?w=1600&auto=format&q=80`;
  const faqs = CITY_FAQS(cityCap);

  return (
    <div>
      <SEO
        title={title}
        description={description}
        path={`/rent/${key}`}
        image={heroImg}
        keywords={`${cityCap} rent, ${cityCap} flats, ${cityCap} 2BHK, ${cityCap} apartments, ${meta.localities.slice(0, 4).join(" rent, ")} rent, no brokerage ${cityCap}, owner rental ${cityCap}, PG in ${cityCap}`}
        breadcrumbs={[
          { name: "Rent", path: "/properties?listing_type=rent" },
          { name: cityCap, path: `/rent/${key}` },
        ]}
        faqs={faqs}
      />

      {/* Hero */}
      <section className="relative min-h-[52vh] flex items-end overflow-hidden">
        <img src={heroImg} alt={`${cityCap} — ${meta.landmark}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-16 pt-32 w-full text-white">
          <Breadcrumbs items={[
            { name: "Rent", path: "/properties?listing_type=rent" },
            { name: cityCap, path: `/rent/${key}` },
          ]} />
          <p className="label-eyebrow text-[#C9A24A] mb-3">{meta.state} · {meta.landmark}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl mb-4">
            Homes for rent in <em className="not-italic text-[#C9A24A]">{cityCap}</em>
          </h1>
          <p className="text-white/85 max-w-2xl text-lg">
            {count > 0
              ? <><b className="text-white">{count}</b> verified listings from real owners across {cityCap}.</>
              : <>Fresh {cityCap} listings from real owners land every week.</>}
            {" "}Zero brokerage. Free reveal for your first 10 owner contacts.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 space-y-14">
        {/* Trust bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: <BadgeCheck size={18} />, k: "Verified owners", v: "Government-ID reviewed" },
            { icon: <TrendingUp size={18} />, k: "AI Rent Estimator", v: `Fair rent for ${cityCap}` },
            { icon: <Users size={18} />, k: "Direct contact", v: "No brokerage, ever" },
            { icon: <Sparkles size={18} />, k: "Discover Scroll", v: "Tinder-style browsing" },
          ].map((c) => (
            <div key={c.k} className="bg-white border border-[#EFE3D0] rounded-2xl p-5 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A] shrink-0">{c.icon}</div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{c.k}</p>
                <p className="text-xs text-gray-500 mt-0.5">{c.v}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Popular localities */}
        <section>
          <p className="label-eyebrow text-gray-500 mb-3">Neighbourhoods</p>
          <h2 className="font-display text-3xl text-[#7A1E2A] mb-6">Popular localities in {cityCap}</h2>
          <div className="flex flex-wrap gap-2">
            {meta.localities.map((loc) => (
              <Link
                key={loc}
                to={`/rent/${key}/${loc.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                className="px-4 py-2 rounded-full bg-white border border-[#EFE3D0] text-sm text-gray-700 hover:border-[#7A1E2A] hover:text-[#7A1E2A] transition-colors inline-flex items-center gap-1.5"
                data-testid={`loc-chip-${loc.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <MapPin size={12} /> {loc} rent
              </Link>
            ))}
          </div>
        </section>

        {/* Live listings */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="label-eyebrow text-gray-500 mb-2">Live listings</p>
              <h2 className="font-display text-3xl text-[#7A1E2A]">Latest homes in {cityCap}</h2>
            </div>
            <Link to={`/properties?listing_type=rent&city=${encodeURIComponent(cityCap)}`} className="text-sm text-[#7A1E2A] hover:underline">View all →</Link>
          </div>
          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading listings…</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-[#EFE3D0] rounded-2xl p-10 text-center">
              <p className="font-display text-2xl text-[#7A1E2A] mb-2">First listing coming soon</p>
              <p className="text-gray-500 mb-4">Be the first owner to list a home in {cityCap} — it takes 60 seconds and is free forever.</p>
              <Button onClick={() => nav("/list-property")} className="tlr-gold-btn rounded-full h-11 px-6">List your property</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p) => (
                <PropertyCard key={p.id} property={p} isFav={user && user.favorites?.includes(p.id)} />
              ))}
            </div>
          )}
        </section>

        {/* SEO copy — helps ranking with unique long-form content per city */}
        <section className="bg-white border border-[#EFE3D0] rounded-2xl p-8 md:p-10 prose max-w-none">
          <h2 className="font-display text-3xl text-[#7A1E2A] mb-4">About renting in {cityCap}</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            {cityCap}, {meta.state}, known for its {meta.landmark}, is one of India's most sought-after rental markets.
            Whether you're a working professional relocating to {meta.localities[0]}, a family looking for a 2BHK in {meta.localities[1]},
            or a student searching for a PG near {meta.localities[2]}, To-Let Rentals connects you directly with verified owners
            in every corner of the city — <b>with zero brokerage</b>.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Popular neighbourhoods like {meta.localities.slice(0, 4).join(", ")} attract renters looking for well-connected
            transport, proximity to workplaces, and a mix of independent houses, apartments and gated societies. Our AI Rent
            Estimator uses live listings from across {cityCap} to give you a fair monthly rent range for any locality and BHK
            configuration in seconds.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Every listing on To-Let Rentals is <b>manually reviewed</b> before it goes live, and owners can verify their identity
            for a trusted <b>Verified Owner</b> badge. First-time renters get 10 free owner-contact reveals — no credit card required.
            Start browsing homes in {cityCap} above, or scroll walkthroughs on our Discover feed.
          </p>
        </section>

        {/* Explore other cities — internal linking hub for SEO */}
        <section>
          <p className="label-eyebrow text-gray-500 mb-3">More cities</p>
          <h2 className="font-display text-3xl text-[#7A1E2A] mb-6">Explore other cities</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(CITY_META).filter(([k]) => k !== key).map(([k, m]) => (
              <Link
                key={k}
                to={`/rent/${k}`}
                className="px-4 py-2 rounded-full bg-white border border-[#EFE3D0] text-sm text-gray-700 hover:border-[#7A1E2A] hover:text-[#7A1E2A] transition-colors inline-flex items-center gap-1.5 capitalize"
                data-testid={`city-link-${k}`}
              >
                Rent in {k}
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ — visible + FAQPage JSON-LD is emitted via <SEO faqs={...}> above */}
        <FAQSection faqs={faqs} title={`FAQs about renting in ${cityCap}`} />
      </div>
    </div>
  );
}
